import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, HeartHandshake, Coins } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar
} from "recharts";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function FleetLedger({ vessel }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    description: "",
    amount: "",
    partner_split: "70/30",
    partner_a: "EIGOO (Director K)",
    partner_b: "Joint Partner",
  });

  // Responsibility Migration preview math (matches backend _compute_splits)
  const previewSplits = (amount, split) => {
    const a = parseFloat(amount || 0);
    if (!a) return null;
    const academy = +(a * 0.10).toFixed(2);
    if (split === "50/50") {
      const pa = +(a * 0.45).toFixed(2);
      const pb = +(a - academy - pa).toFixed(2);
      return { academy, pa, pb, source: "Shared Burden" };
    }
    const pb = +(a * 0.30).toFixed(2);
    const pa = +(a - academy - pb).toFixed(2);
    return { academy, pa, pb, source: "EIGOO Funded" };
  };

  const load = async () => {
    const [e, s] = await Promise.all([
      api.get("/ledger"),
      api.get("/ledger/summary"),
    ]);
    setEntries(e.data);
    setSummary(s.data);
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(
    () => entries.filter((e) => e.vessel === vessel),
    [entries, vessel]
  );

  const vesselTotal = visible.reduce((a, b) => a + b.amount, 0);
  const vesselCK = visible.reduce((a, b) => a + b.cyberkids_share, 0);
  const vesselA = visible.reduce((a, b) => a + b.partner_a_share, 0);
  const vesselB = visible.reduce((a, b) => a + b.partner_b_share, 0);

  const timeseries = useMemo(() => {
    const map = {};
    visible.forEach((e) => {
      const d = e.created_at.slice(0, 10);
      map[d] = (map[d] || 0) + e.amount;
    });
    return Object.entries(map)
      .map(([date, amount]) => ({ date: date.slice(5), amount }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [visible]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger", {
        ...form,
        vessel,
        amount: parseFloat(form.amount),
      });
      setForm({ ...form, description: "", amount: "" });
      setShowForm(false);
      toast.success("Ledger entry sealed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/ledger/${id}`);
    toast.success("Entry stricken from the ledger.");
    load();
  };

  const pieData = [
    { name: "EIGOO Net", value: vesselA, color: "#D8AB4E" },
    { name: "Partner Net", value: vesselB, color: "#C1432E" },
    { name: "Academy 10%", value: vesselCK, color: "#F2C568" },
  ];

  return (
    <div className="space-y-6" data-testid="fleet-ledger">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono text-[#D8AB4E]">MODULE 02 // FLEET LEDGER</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-1">Treasury &amp; Sovereign Splits</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            <span className="text-[#D8AB4E]">Responsibility Migration Protocol</span> ·
            <span className="text-zinc-300"> Tier 1 (70/30):</span> EIGOO 60% / Partner 30% / Academy 10% — <span className="text-[#D8AB4E]">EIGOO Funded</span> ·
            <span className="text-zinc-300"> Tier 2 (50/50):</span> EIGOO 45% / Partner 45% / Academy 10% — <span className="text-[#E55A45]">Shared Burden</span>
          </p>
        </div>
        <button
          data-testid="ledger-add-button"
          onClick={() => setShowForm((v) => !v)}
          className="btn-gold px-5 py-3 text-[11px] inline-flex items-center gap-2"
        >
          <Plus size={14} /> {showForm ? "CLOSE" : "RECORD ENTRY"}
        </button>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Vessel Revenue" value={fmt(vesselTotal)} accent="#D8AB4E" />
        <StatCard icon={Coins} label="EIGOO Net" value={fmt(vesselA)} accent="#F2C568" />
        <StatCard icon={Coins} label="Partner Net" value={fmt(vesselB)} accent="#A1A1AA" />
        <StatCard icon={HeartHandshake} label="Academy Tax (10%)" value={fmt(vesselCK)} accent="#C1432E" />
      </div>

      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="glass-panel p-6 grid md:grid-cols-12 gap-4"
          data-testid="ledger-form"
        >
          <div className="md:col-span-5">
            <label className="label-mono text-zinc-400 mb-2 block">Description</label>
            <input
              data-testid="ledger-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="Q1 Strategic Capital Allocation Fee"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono text-zinc-400 mb-2 block">Amount USD</label>
            <input
              data-testid="ledger-amount-input"
              type="number" min="0" step="0.01" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="48500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono text-zinc-400 mb-2 block">Tier</label>
            <select
              data-testid="ledger-split-select"
              value={form.partner_split}
              onChange={(e) => setForm({ ...form, partner_split: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
            >
              <option value="70/30">Tier 1 · 70 / 30 · EIGOO Funded</option>
              <option value="50/50">Tier 2 · 50 / 50 · Shared Burden</option>
            </select>
          </div>
          <div className="md:col-span-3 flex items-end">
            <button data-testid="ledger-submit-button" className="btn-gold w-full py-2.5 text-[11px]">
              SEAL ENTRY
            </button>
          </div>
          {/* Live preview of the protocol math */}
          {(() => {
            const p = previewSplits(form.amount, form.partner_split);
            if (!p) return null;
            return (
              <div className="md:col-span-12 grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-[#D8AB4E]/10" data-testid="ledger-preview">
                <PreviewBox label="EIGOO Net" value={fmt(p.pa)} color="#F2C568" />
                <PreviewBox label="Partner Net" value={fmt(p.pb)} color="#A1A1AA" />
                <PreviewBox label="Academy Tax" value={fmt(p.academy)} color="#E55A45" />
                <PreviewBox label="Source" value={p.source} color={p.source === "EIGOO Funded" ? "#D8AB4E" : "#E55A45"} />
              </div>
            );
          })()}
        </motion.form>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-panel p-6 lg:col-span-2 relative grain">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono text-[#D8AB4E]">REVENUE FLOW · {vessel.toUpperCase()}</div>
              <div className="font-display text-xl mt-1">Capital Trajectory</div>
            </div>
            <div className="font-mono-data text-xs text-zinc-500">{visible.length} ENTRIES</div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <AreaChart data={timeseries}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D8AB4E" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#D8AB4E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(216,171,78,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke="#52525b" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <YAxis stroke="#52525b" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: "#040404", border: "1px solid rgba(216,171,78,0.4)", fontFamily: "JetBrains Mono", fontSize: 12 }}
                  labelStyle={{ color: "#D8AB4E" }}
                />
                <Area type="monotone" dataKey="amount" stroke="#D8AB4E" strokeWidth={2} fill="url(#goldFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-panel p-6 relative grain">
          <div className="label-mono text-[#D8AB4E] mb-1">DISTRIBUTION</div>
          <div className="font-display text-xl mb-4">Sovereign Splits</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={55} outerRadius={85} stroke="#040404" paddingAngle={2}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#040404", border: "1px solid rgba(216,171,78,0.4)", fontFamily: "JetBrains Mono", fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {pieData.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-xs font-mono-data">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2" style={{ background: p.color }} /> {p.name}
                </div>
                <span className="text-zinc-300">{fmt(p.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Entries list */}
      <div className="glass-panel relative grain">
        <div className="p-5 flex items-center justify-between border-b border-[#D8AB4E]/15">
          <div>
            <div className="label-mono text-[#D8AB4E]">LEDGER MANIFEST</div>
            <div className="font-display text-xl mt-1">Recent Entries</div>
          </div>
        </div>
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-2 border-b border-[#D8AB4E]/10 label-mono text-zinc-500" data-testid="ledger-manifest-header">
          <div className="col-span-4">Description</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Academy Source</div>
          <div className="col-span-1">EIGOO</div>
          <div className="col-span-1">Partner</div>
          <div className="col-span-1">Academy</div>
          <div className="col-span-1 text-right">Strike</div>
        </div>
        <div className="divide-y divide-[#D8AB4E]/10">
          {visible.length === 0 && (
            <div className="p-10 text-center text-zinc-500 font-mono-data text-sm">
              No entries yet for this vessel. Record your first capital event.
            </div>
          )}
          {visible.map((e) => (
            <div key={e.id} className="p-5 grid grid-cols-12 gap-3 items-center hover:bg-[#D8AB4E]/[0.03] transition-colors" data-testid={`ledger-entry-${e.id}`}>
              <div className="col-span-12 md:col-span-4">
                <div className="text-sm">{e.description}</div>
                <div className="font-mono-data text-[10px] text-zinc-500 mt-0.5">
                  {new Date(e.created_at).toLocaleString()} · TIER {e.partner_split}
                </div>
              </div>
              <div className="col-span-6 md:col-span-2 font-mono-data text-sm">{fmt(e.amount)}</div>
              <div className="col-span-6 md:col-span-2" data-testid={`ledger-source-${e.id}`}>
                <span
                  className="inline-block px-2 py-1 text-[9px] font-mono-data tracking-[0.18em] border"
                  style={{
                    color: e.academy_source === "EIGOO Funded" ? "#D8AB4E" : e.academy_source === "Shared Burden" ? "#E55A45" : "#A1A1AA",
                    borderColor: (e.academy_source === "EIGOO Funded" ? "#D8AB4E" : e.academy_source === "Shared Burden" ? "#E55A45" : "#A1A1AA") + "55",
                  }}
                >
                  {(e.academy_source || "—").toUpperCase()}
                </span>
              </div>
              <div className="col-span-4 md:col-span-1 font-mono-data text-xs text-[#D8AB4E]">E: {fmt(e.partner_a_share)}</div>
              <div className="col-span-4 md:col-span-1 font-mono-data text-xs text-zinc-300">P: {fmt(e.partner_b_share)}</div>
              <div className="col-span-3 md:col-span-1 font-mono-data text-xs text-[#E55A45]">A: {fmt(e.cyberkids_share)}</div>
              <div className="col-span-1 md:col-span-1 flex justify-end">
                <button
                  data-testid={`ledger-delete-${e.id}`}
                  onClick={() => remove(e.id)}
                  className="text-zinc-500 hover:text-[#E55A45] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Global summary mini */}
      {summary && (
        <div className="glass-panel p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="label-mono text-zinc-500">ARMADA WIDE</div>
            <div className="flex flex-wrap gap-6 font-mono-data text-xs">
              <div>TOTAL <span className="text-[#D8AB4E] ml-2">{fmt(summary.total_revenue)}</span></div>
              <div>CYBERKIDS <span className="text-[#E55A45] ml-2">{fmt(summary.cyberkids_total)}</span></div>
              <div>ENTRIES <span className="text-white ml-2">{summary.entries_count}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="glass-panel p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: accent, opacity: 0.45 }} />
      <div className="flex items-center justify-between">
        <div className="label-mono text-zinc-500">{label}</div>
        <Icon size={14} style={{ color: accent }} />
      </div>
      <div className="font-mono-data text-2xl mt-3" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function PreviewBox({ label, value, color }) {
  return (
    <div className="border border-[#D8AB4E]/15 px-3 py-2">
      <div className="label-mono text-zinc-500">{label}</div>
      <div className="font-mono-data text-sm mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
